import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    className = '',
    disabled,
    ...props
}) => {
    const baseStyles = `
        inline-flex items-center justify-center rounded-xl font-bold
        transition-all duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40
        focus-visible:ring-offset-2 focus-visible:ring-offset-deep-black
        relative overflow-hidden
    `;

    const variants = {
        primary: `
            bg-gradient-to-r from-brand to-brand-light text-black
            shadow-lg shadow-brand/25
            hover:shadow-xl hover:shadow-brand/30 hover:brightness-110
            active:scale-[0.98]
            before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent
            before:translate-x-[-200%] before:transition-transform before:duration-700
            hover:before:translate-x-[200%]
        `,
        secondary: `
            bg-white/[0.05] backdrop-blur-sm border border-white/[0.08]
            text-white/80
            hover:bg-white/[0.08] hover:text-white hover:border-white/[0.15]
        `,
        outline: `
            border border-brand/25 text-brand bg-brand/[0.03]
            hover:bg-brand/[0.08] hover:border-brand/50
        `,
        ghost: `
            text-white/50 hover:text-white hover:bg-white/[0.05]
        `,
        danger: `
            bg-danger/[0.08] text-danger border border-danger/15
            hover:bg-danger/[0.15] hover:border-danger/30
        `
    };

    const sizes = {
        sm: 'px-3.5 py-2 text-xs',
        md: 'px-5 py-2.5 text-sm',
        lg: 'px-8 py-3.5 text-base'
    };

    // Premium gradient spinner for loading state
    const LoadingSpinner = () => (
        <div className="relative w-4 h-4 mr-2">
            {/* Outer ring - rotates clockwise */}
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-current opacity-60 animate-spin" />
            {/* Inner ring - rotates counter-clockwise */}
            <div className="absolute inset-1 rounded-full border-2 border-transparent border-t-current opacity-30 animate-[spin_1s_linear_infinite_reverse]" />
        </div>
    );

    return (
        <button
            className={`
                ${baseStyles}
                ${variants[variant]}
                ${sizes[size]}
                ${fullWidth ? 'w-full' : ''}
                ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}
                ${className}
            `}
            disabled={isLoading || disabled}
            {...props}
        >
            {isLoading && <LoadingSpinner />}
            {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
            <span className="relative z-10">{children}</span>
            {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
        </button>
    );
};
